/***
 *
 *   FORM THEME
 *   Applies a customer-authored SurveyJS theme to a survey model, and derives the
 *   onboarding frame colour from it.
 *
 *   Two rules shape this module:
 *
 *   1. Never hand a stored theme straight to applyTheme. survey-core assigns every
 *      key onto the survey model, and completedHtml / completedBeforeHtml /
 *      loadingHtml render through dangerouslySetInnerHTML. The server allowlists on
 *      write; this rebuilds from an allowlist on render, so anything already in the
 *      column — or written by a path that bypassed the controller — stays inert.
 *
 *   2. Colours are parsed and re-serialised before they reach the pixel path, so
 *      raw customer text is never interpolated into a CSS declaration.
 *
 *   3. It also sanitises the MODEL, not just the theme: scrubSurveyImages clears
 *      any logo or question imageLink that is not a public https URL. Rows
 *      written before the server validated that column can still carry one.
 *      applyFormTheme is NOT a universal chokepoint — only seven of the
 *      seventeen survey-model sites route through it — so views that render a
 *      stored schema without a theme call scrubSurveyImages directly.
 *
 **********/

import { useContext, useEffect } from "react";
import { ViewContext } from "components/lib";
import { writeBrandColor } from "./brandColorCache";

// Mirrors model/formTheme.js ALLOWED_KEYS. Kept deliberately short.
const ALLOWED_KEYS = [
  "cssVariables",
  "themeName",
  "colorPalette",
  "isPanelless",
  "backgroundImage",
  "backgroundOpacity",
  "backgroundImageAttachment",
  "backgroundImageFit",
];

const CSS_VAR_NAME = /^--sjs-[A-Za-z0-9-]+$/;
const FORBIDDEN = ["url(", "image-set(", "expression(", "data:", "/*", "*/", "\\", ";", "{", "}", "<", ">"];

export function hasTheme(theme) {
  return !!(
    theme &&
    typeof theme === "object" &&
    !Array.isArray(theme) &&
    theme.cssVariables &&
    Object.keys(theme.cssVariables).length
  );
}

function valueIsSafe(value) {
  if (typeof value === "number") return true;
  if (typeof value !== "string") return false;
  const lowered = value.toLowerCase();
  return !FORBIDDEN.some((bad) => lowered.includes(bad));
}

/* Build a fresh object carrying only allowlisted keys and safe values. */
export function safeTheme(theme) {
  if (!theme || typeof theme !== "object" || Array.isArray(theme)) return null;

  const out = {};
  ALLOWED_KEYS.forEach((key) => {
    if (!(key in theme)) return;
    if (key === "cssVariables") return;
    if (valueIsSafe(theme[key])) out[key] = theme[key];
  });

  const vars = theme.cssVariables;
  if (vars && typeof vars === "object" && !Array.isArray(vars)) {
    const safeVars = {};
    Object.keys(vars).forEach((name) => {
      if (!CSS_VAR_NAME.test(name)) return;
      if (!valueIsSafe(vars[name])) return;
      safeVars[name] = vars[name];
    });
    if (Object.keys(safeVars).length) out.cssVariables = safeVars;
  }

  return Object.keys(out).length ? out : null;
}

/* Apply a stored theme to a survey model. No-op without one, so the stock
   defaultV2 styling every view already applies remains in place. */
export function applyFormTheme(model, theme) {
  if (!model || typeof model.applyTheme !== "function") return model;

  const safe = safeTheme(theme);
  // safeTheme() already returns null when nothing allowlisted survived, so any
  // object reaching here carries at least one key worth applying. Gating on
  // cssVariables specifically would silently drop a theme whose only setting is a
  // background image. The Creator never emits one (ThemeModel.toJSON always writes
  // cssVariables), but a theme written by the API, a fixture, or a future Creator
  // version can — and losing it without a trace is the failure mode this module
  // exists to avoid.
  // The scrub must run AFTER applyTheme, not before: applyTheme assigns every
  // allowlisted key — backgroundImage included — straight onto the model, so
  // scrubbing first would be undone on exactly the themed public pages the
  // scrub exists to protect.
  if (!safe) return scrubSurveyImages(model);

  model.applyTheme(safe);
  return scrubSurveyImages(model);
}

/* ---------------------------------------------------------------- colour ---- */

function parseColor(value) {
  if (typeof value !== "string") return null;
  const input = value.trim();

  const hex = input.match(/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/);
  if (hex) {
    const raw = hex[1];
    const full =
      raw.length === 3
        ? raw
            .split("")
            .map((c) => c + c)
            .join("")
        : raw;
    return {
      r: parseInt(full.slice(0, 2), 16),
      g: parseInt(full.slice(2, 4), 16),
      b: parseInt(full.slice(4, 6), 16),
    };
  }

  // Real Creator themes mix formats: a hex primary alongside an rgba() -dark sibling.
  const rgb = input.match(/^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)/i);
  if (rgb) {
    const [r, g, b] = [rgb[1], rgb[2], rgb[3]].map((n) => Math.round(parseFloat(n)));
    if ([r, g, b].some((n) => Number.isNaN(n) || n < 0 || n > 255)) return null;
    return { r, g, b };
  }

  const hsl = input.match(/^hsla?\(\s*([\d.]+)[\s,]+([\d.]+)%[\s,]+([\d.]+)%/i);
  if (hsl) return hslToRgb(parseFloat(hsl[1]), parseFloat(hsl[2]) / 100, parseFloat(hsl[3]) / 100);

  return null;
}

function hslToRgb(h, s, l) {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  const seg = Math.floor(((h % 360) + 360) % 360 / 60);
  const table = [
    [c, x, 0],
    [x, c, 0],
    [0, c, x],
    [0, x, c],
    [x, 0, c],
    [c, 0, x],
  ][seg];
  return {
    r: Math.round((table[0] + m) * 255),
    g: Math.round((table[1] + m) * 255),
    b: Math.round((table[2] + m) * 255),
  };
}

function serialize({ r, g, b }) {
  return `rgb(${r}, ${g}, ${b})`;
}

/*
 * Relative luminance per WCAG. A brand Row hardcodes white text; a light customer
 * primary (the stock orange, for example) would make public headings unreadable,
 * so the text colour follows the background rather than assuming it stays dark.
 */
function relativeLuminance({ r, g, b }) {
  const channel = (value) => {
    const v = value / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

const DARK_TEXT = "#1F2937";
const LIGHT_TEXT = "#FFFFFF";

function contrastRatio(a, b) {
  const [hi, lo] = a > b ? [a, b] : [b, a];
  return (hi + 0.05) / (lo + 0.05);
}

/*
 * Pick whichever of white/dark actually wins on contrast, rather than testing the
 * background against a luminance threshold. A threshold gets mid-tones wrong: the
 * stock SurveyJS orange sits just under any sensible cutoff yet scores only 2.1:1
 * against white, while the existing purple genuinely does read better on white.
 */
export function readableTextColor(value) {
  const parsed = parseColor(value);
  if (!parsed) return null;
  const background = relativeLuminance(parsed);
  const onLight = contrastRatio(background, relativeLuminance({ r: 255, g: 255, b: 255 }));
  const onDark = contrastRatio(background, relativeLuminance({ r: 31, g: 41, b: 55 }));
  return onDark > onLight ? DARK_TEXT : LIGHT_TEXT;
}

function darken({ r, g, b }, amount = 0.18) {
  const shift = (channel) => Math.max(0, Math.round(channel * (1 - amount)));
  return { r: shift(r), g: shift(g), b: shift(b) };
}

/* The primary colour a theme is built around, canonicalised. */
export function themePrimaryColor(theme) {
  const safe = safeTheme(theme);
  const parsed = parseColor(safe?.cssVariables?.["--sjs-primary-backcolor"]);
  return parsed ? serialize(parsed) : null;
}

/*
 * Frame gradient stops. Today's frame runs darker at the top (#6A6AB8) into the
 * brighter brand primary at the bottom (#8C66F2); this reproduces that
 * relationship. Returns null — never a malformed string — when the primary is
 * missing or unparseable, because an invalid colour inside linear-gradient()
 * invalidates the whole declaration and the frame would render with no
 * background at all rather than falling back.
 */
export function brandGradient(theme) {
  const safe = safeTheme(theme);
  const vars = safe?.cssVariables;
  if (!vars) return null;

  const primary = parseColor(vars["--sjs-primary-backcolor"]);
  if (!primary) return null;

  const dark = parseColor(vars["--sjs-primary-backcolor-dark"]);
  return {
    from: serialize(dark || darken(primary)),
    to: serialize(primary),
  };
}

/* ----------------------------------------------------------------- hook ---- */

/*
 * Publish the frame colour for this route.
 *
 * ViewContext is created above the layout, so the layout and any Row can read
 * what a descendant view publishes. Scoping it here rather than on
 * document.documentElement means the colour cannot outlive the route: there is no
 * cleanup discipline for a future view to get wrong, and the authenticated app
 * chrome is unreachable by construction.
 */
export function useFormBrandColor(theme, payloadResolved) {
  const context = useContext(ViewContext);
  const setBrandColor = context?.setBrandColor;
  const gradient = brandGradient(theme);
  const from = gradient?.from || null;
  const to = gradient?.to || null;
  // Has the payload landed? Inferring that from `theme !== undefined` is only a
  // fallback, because a resolved payload can legitimately carry no theme at all —
  // a closed intake link (`{ open: false }`) or a cancelled verification — which is
  // indistinguishable from "still loading" by the value alone, and would leave a
  // stale cache row behind. A caller that knows its own load state passes it.
  // Pass a real boolean, and only ever `false` together with an absent theme: the
  // cleanup below is unconditional while the publish is not, so a caller that
  // changed the theme while reporting `false` would clear the frame without
  // republishing it.
  const resolved =
    payloadResolved === undefined ? theme !== undefined : !!payloadResolved;

  useEffect(() => {
    if (!setBrandColor) return undefined;

    // Publish only once resolved. Publishing on the loading render would push null
    // over the colour View seeded from the session cache and repaint the default
    // frame for the whole fetch — brand -> default -> brand, which is worse than
    // the single blink the seed exists to remove.
    if (resolved) {
      const value = from && to ? { from, to } : null;
      setBrandColor(value);
      writeBrandColor(value);
    }

    // Registered unconditionally: a colour must never outlive its route (KTD6).
    return () => setBrandColor(null);
  }, [setBrandColor, from, to, resolved]);
}

// Mirrors model/imageUrl.js PUBLIC_IMAGE_URL and MAX_IMAGE_URL_LENGTH. Mirrored
// rather than imported because CRA's ModuleScopePlugin forbids importing
// anything outside client/src — the same reason ALLOWED_KEYS above is a copy.
// test/image-url.js pins the two identical so they cannot drift.
const PUBLIC_IMAGE_URL =
  /^https:\/\/[a-z0-9-]+(?:\.[a-z0-9-]+)+(?::\d{2,5})?(?:\/[a-z0-9._~%!$&'()*+,;=:@\/-]*)?(?:\?[a-z0-9._~%!$&'()*+,;=:@\/?-]*)?$/i;
const MAX_IMAGE_URL_LENGTH = 500;

export function isRenderableImageUrl(value) {
  if (typeof value !== "string") return false;
  const trimmed = value.trim();
  if (!trimmed || trimmed.length > MAX_IMAGE_URL_LENGTH) return false;
  return PUBLIC_IMAGE_URL.test(trimmed);
}

/*
 * scrubSurveyImages()
 * clear any image on the model that we would not render on a public page.
 *
 * Defence in depth for rows written before the server validated form.data: a
 * failed upload used to be able to write an arbitrary string into survey.logo
 * or a question's imageLink, and survey-react-ui puts both straight into an
 * <img src>. Call this anywhere a STORED schema becomes a model — the test is
 * "did this schema come from the server?", not a list of filenames.
 */

function scrubQuestion(question, depth = 0) {
  if (!question || depth > 10) return;

  if (question.imageLink && !isRenderableImageUrl(question.imageLink))
    question.imageLink = "";

  if (Array.isArray(question.choices)) {
    for (const choice of question.choices) {
      if (choice?.imageLink && !isRenderableImageUrl(choice.imageLink))
        choice.imageLink = "";
    }
  }

  // A paneldynamic with no rows yet exposes nothing through getAllQuestions —
  // its questions live on the template, and the template is what is stored.
  for (const nested of question.template?.questions || [])
    scrubQuestion(nested, depth + 1);

  for (const nested of question.templateElements || [])
    scrubQuestion(nested, depth + 1);
}

export function scrubSurveyImages(model) {
  if (!model) return model;

  if (model.logo && !isRenderableImageUrl(model.logo)) model.logo = "";

  // backgroundImage is interpolated into a CSS url(...) rather than an <img src>,
  // so it is the one image property where a stored string reaches a style sink
  if (model.backgroundImage && !isRenderableImageUrl(model.backgroundImage))
    model.backgroundImage = "";

  if (typeof model.getAllQuestions === "function") {
    // (visibleOnly, includeDesignTime, includeNested) — the default skips
    // questions inside a matrix detail panel, which is exactly where a legacy
    // poisoned imageLink can still be hiding
    for (const question of model.getAllQuestions(false, false, true))
      scrubQuestion(question);
  }

  return model;
}
